import { MEDIA_LIMITS } from "./contract.ts";
import type { ClipboardSet, Direction } from "./types.ts";

export const CLIPBOARD_CHANNEL_MAX_UTF8 = 262144;

const encoder = new TextEncoder();

export function clipboardMessageUtf8Bytes(message: ClipboardSet): number {
  return encoder.encode(JSON.stringify(message)).length;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const step = 0x8000;
  for (let index = 0; index < bytes.length; index += step) {
    const slice = bytes.subarray(index, index + step);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

function base64ToBytes(payload: string): Uint8Array | undefined {
  try {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  } catch {
    return undefined;
  }
}

function wholeMessage(input: {
  bytes: Uint8Array;
  mime: "text/plain" | "image/png";
  direction: Direction;
  id: string;
  ts: string;
}): ClipboardSet {
  return {
    v: 1,
    type: "clipboard.set",
    id: input.id,
    ts: input.ts,
    direction: input.direction,
    mime: input.mime,
    byteLength: input.bytes.byteLength,
    payloadBase64: bytesToBase64(input.bytes),
  };
}

function partMessage(
  input: {
    mime: "text/plain" | "image/png";
    direction: Direction;
    id: string;
    ts: string;
    total: number;
  },
  slice: Uint8Array,
  partIndex: number,
  partCount: number,
): ClipboardSet {
  return {
    v: 1,
    type: "clipboard.set",
    id: input.id,
    ts: input.ts,
    direction: input.direction,
    mime: input.mime,
    byteLength: input.total,
    payloadBase64: bytesToBase64(slice),
    partIndex,
    partCount,
  };
}

function sizedPart(
  input: {
    mime: "text/plain" | "image/png";
    direction: Direction;
    id: string;
    ts: string;
    total: number;
  },
  sliceBytes: number,
  partIndex: number,
  partCount: number,
): ClipboardSet {
  const payloadBase64 = "A".repeat(4 * Math.ceil(sliceBytes / 3));
  return {
    v: 1,
    type: "clipboard.set",
    id: input.id,
    ts: input.ts,
    direction: input.direction,
    mime: input.mime,
    byteLength: input.total,
    payloadBase64,
    partIndex,
    partCount,
  };
}

function largestSlice(input: {
  bytes: Uint8Array;
  mime: "text/plain" | "image/png";
  direction: Direction;
  id: string;
  ts: string;
}): number {
  const total = input.bytes.byteLength;
  let low = 1;
  let high = total - 1;
  let best = 0;
  const probe = { mime: input.mime, direction: input.direction, id: input.id, ts: input.ts, total };
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const count = Math.ceil(total / mid);
    const sample = sizedPart(probe, mid, count - 1, count);
    if (clipboardMessageUtf8Bytes(sample) <= CLIPBOARD_CHANNEL_MAX_UTF8) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  if (best === 0) throw new Error("clipboard part exceeds the data channel cap");
  return best;
}

export function planClipboardWire(input: {
  bytes: Uint8Array;
  mime: "text/plain" | "image/png";
  direction: Direction;
  id: string;
  ts: string;
}): ClipboardSet[] {
  const whole = wholeMessage(input);
  if (clipboardMessageUtf8Bytes(whole) <= CLIPBOARD_CHANNEL_MAX_UTF8) return [whole];

  const slice = largestSlice(input);
  const count = Math.ceil(input.bytes.byteLength / slice);
  const messages: ClipboardSet[] = [];
  for (let index = 0; index < count; index += 1) {
    const start = index * slice;
    const part = input.bytes.subarray(start, Math.min(start + slice, input.bytes.byteLength));
    const message = partMessage(
      { mime: input.mime, direction: input.direction, id: input.id, ts: input.ts, total: input.bytes.byteLength },
      part,
      index,
      count,
    );
    if (clipboardMessageUtf8Bytes(message) > CLIPBOARD_CHANNEL_MAX_UTF8) {
      throw new Error("clipboard part exceeds the data channel cap");
    }
    messages.push(message);
  }
  return messages;
}

export type ClipboardInboxStep =
  | { status: "pending" }
  | { status: "drop" }
  | { status: "ready"; message: ClipboardSet };

type OpenTransfer = {
  id: string;
  count: number;
  total: number;
  direction: Direction;
  mime: "text/plain" | "image/png";
  v: 1;
  ts: string;
  parts: Array<Uint8Array | undefined>;
  got: number;
  buffered: number;
};

export function createClipboardInbox(): { push(message: ClipboardSet): ClipboardInboxStep } {
  let current: OpenTransfer | null = null;

  function drop(): ClipboardInboxStep {
    current = null;
    return { status: "drop" };
  }

  return {
    push(message) {
      const hasIndex = message.partIndex !== undefined;
      const hasCount = message.partCount !== undefined;
      if (!hasIndex && !hasCount) {
        current = null;
        return { status: "ready", message };
      }
      if (!hasIndex || !hasCount) return drop();
      const partIndex = message.partIndex ?? -1;
      const partCount = message.partCount ?? 0;
      if (
        partCount < 2 ||
        partCount > 64 ||
        partIndex < 0 ||
        partIndex >= partCount ||
        message.byteLength < 0 ||
        message.byteLength > MEDIA_LIMITS.maxClipboardBytes
      ) {
        return drop();
      }
      const slice = base64ToBytes(message.payloadBase64);
      if (!slice || slice.byteLength === 0 || slice.byteLength > message.byteLength) return drop();
      if (
        !current ||
        current.id !== message.id ||
        current.count !== partCount ||
        current.total !== message.byteLength ||
        current.direction !== message.direction ||
        current.mime !== message.mime
      ) {
        if (current && current.id === message.id) return drop();
        current = {
          id: message.id,
          count: partCount,
          total: message.byteLength,
          direction: message.direction,
          mime: message.mime,
          v: 1,
          ts: message.ts,
          parts: new Array(partCount),
          got: 0,
          buffered: 0,
        };
      }
      if (current.parts[partIndex] || current.buffered + slice.byteLength > current.total) return drop();
      current.parts[partIndex] = slice;
      current.buffered += slice.byteLength;
      current.got += 1;
      if (current.got < current.count) return { status: "pending" };
      const joined = new Uint8Array(current.total);
      let offset = 0;
      for (const part of current.parts) {
        if (!part) return drop();
        joined.set(part, offset);
        offset += part.byteLength;
      }
      const ready: ClipboardSet = {
        v: current.v,
        type: "clipboard.set",
        id: current.id,
        ts: current.ts,
        direction: current.direction,
        mime: current.mime,
        byteLength: current.total,
        payloadBase64: bytesToBase64(joined),
      };
      current = null;
      return { status: "ready", message: ready };
    },
  };
}
